import { request } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';
import {
  deleteArretePointagesReglement,
  getArretePointagesReglement,
  postArretePointagesReglement,
} from '@/routes';
import type { ArretePointageReglementType, ArretePointageSelectionType } from '@/types';

/**
 * Charge les pointages existants (règlements pointés sur le prévisionnel) depuis l'API et
 * expose un Set des identifiants + fonctions de mise à jour avec optimistic UI et rollback.
 *
 * Active (= active fetch) est passé en argument : typiquement lié à la présence d'une régie.
 */
const useArretePointageSelection = (active: boolean): ArretePointageSelectionType => {
  const [selected, setSelected] = React.useState<Set<number>>(() => new Set());
  const [loading, setLoading] = React.useState<boolean>(true);
  const [saving, setSaving] = React.useState<boolean>(false);

  // Miroir synchrone de `selected` : nécessaire pour calculer des diffs (applyBulk)
  // sans dépendre de l'exécution différée des updater de useState en React 18.
  const selectedRef = React.useRef<Set<number>>(new Set());

  // Dédoublonnage : ListeTable de @mairistem/liste appelle `onSelect(data, selectedElements)`
  // à l'INTÉRIEUR d'un `setSelectedItems(prev => ...)`. En React StrictMode (dev) l'updater
  // est ré-invoqué pour valider sa pureté ; comme notre optimistic update + la propagation
  // via useEffect peuvent avoir modifié le state interne de ListeTable entre les deux
  // invocations, le 2e calcul part d'un prev différent et renvoie l'opération inverse
  // (POST puis DELETE pour un seul clic). On absorbe ce doublon avec un lock par id.
  const inFlightRef = React.useRef<Set<number>>(new Set());

  const updateSelected = React.useCallback((updater: (prev: Set<number>) => Set<number>) => {
    const next = updater(selectedRef.current);
    selectedRef.current = next;
    setSelected(next);
  }, []);

  // Chargement initial des pointages existants.
  React.useEffect(() => {
    if (!active) {
      setLoading(false);
      return () => {};
    }

    let cancelled = false;
    setLoading(true);

    request
      .get(getArretePointagesReglement())
      .then((response: unknown) => {
        if (cancelled) return;
        // @mairistem/tools enveloppe la réponse : { data: [...] } ou directement [...].
        const payload =
          (response as { data?: ArretePointageReglementType[] }).data ??
          (response as ArretePointageReglementType[]);
        const ids = (payload || []).map((p) => p.identifiantReglement);
        const nextSet = new Set(ids);
        selectedRef.current = nextSet;
        setSelected(nextSet);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Erreur lors du chargement des règlements déjà pointés.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active]);

  const toggle = React.useCallback(
    async (identifiantReglement: number, nextChecked: boolean) => {
      if (inFlightRef.current.has(identifiantReglement)) return;
      inFlightRef.current.add(identifiantReglement);
      setSaving(true);

      // Optimistic update
      updateSelected((prev) => {
        const next = new Set(prev);
        if (nextChecked) next.add(identifiantReglement);
        else next.delete(identifiantReglement);
        return next;
      });

      try {
        if (nextChecked) {
          await request.post(postArretePointagesReglement(), {
            identifiantReglements: [identifiantReglement],
          });
        } else {
          await request.delete(deleteArretePointagesReglement([identifiantReglement]));
        }
      } catch {
        // Rollback
        updateSelected((prev) => {
          const next = new Set(prev);
          if (nextChecked) next.delete(identifiantReglement);
          else next.add(identifiantReglement);
          return next;
        });
        toast.error('Échec de la mise à jour du pointage.');
      } finally {
        inFlightRef.current.delete(identifiantReglement);
        if (inFlightRef.current.size === 0) setSaving(false);
      }
    },
    [updateSelected],
  );

  const applyBulk = React.useCallback(
    async (nextSelected: number[]) => {
      const nextSet = new Set(nextSelected);
      const prev = selectedRef.current;
      const toAdd: number[] = Array.from(nextSet).filter((id) => !prev.has(id));
      const toRemove: number[] = Array.from(prev).filter((id) => !nextSet.has(id));

      // Optimistic update systématique (miroir synchronisé).
      selectedRef.current = nextSet;
      setSelected(nextSet);

      // Pas de diff → pas d'appel API.
      if (toAdd.length === 0 && toRemove.length === 0) return;

      setSaving(true);
      try {
        const promises: Promise<unknown>[] = [];
        if (toAdd.length > 0) {
          promises.push(
            request.post(postArretePointagesReglement(), {
              identifiantReglements: toAdd,
            }),
          );
        }
        if (toRemove.length > 0) {
          promises.push(request.delete(deleteArretePointagesReglement(toRemove)));
        }
        await Promise.all(promises);
      } catch {
        // Rollback : restaurer l'état précédent via diff inverse.
        updateSelected((current) => {
          const rolled = new Set(current);
          toAdd.forEach((id) => rolled.delete(id));
          toRemove.forEach((id) => rolled.add(id));
          return rolled;
        });
        toast.error('Échec de la mise à jour du pointage.');
      } finally {
        setSaving(false);
      }
    },
    [updateSelected],
  );

  return {
    selected,
    loading,
    saving,
    toggle,
    applyBulk,
  };
};

export default useArretePointageSelection;
